import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Calendar, Clock, Video, MapPin, Plus, 
  ExternalLink, Search, CheckCircle, 
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../common/apiClient';
import SummaryApi from '../../common/SummaryApi';
import FormOffCanvas from '../ui/FormOffCanvas';
import SelectDropdown from '../../customStyles/SelectDropdown';
import PageLoader from '../../helpers/PageLoader';

// Static configurations for SelectDropdown options
const ratingOptions = [
  { value: 1, label: '★ 1 - Very Poor' },
  { value: 2, label: '★ 2 - Below Average' },
  { value: 3, label: '★ 3 - Good' },
  { value: 4, label: '★ 4 - Strong Candidate' },
  { value: 5, label: '★ 5 - Excellent Fit' }
];

const recommendationOptions = [
  { value: 'strong_yes', label: 'Strong Yes' },
  { value: 'yes', label: 'Yes / Hire' },
  { value: 'neutral', label: 'Neutral' },
  { value: 'no', label: 'No / Reject' },
  { value: 'strong_no', label: 'Strong No' }
];

const MyAssignedInterviews = () => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Feedback submission states
  const [selectedItem, setSelectedItem] = useState(null); // Holds the selected candidate & round details
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 3,
    comments: '',
    recommendation: 'yes'
  });

  // Fetch pending assigned interviews
  const fetchMyInterviews = useCallback(async () => {
    setLoading(true);
    try {
      // Calls the new route we built: GET /api/hr/recruitment/interviews/my-assigned
      const response = await api.get(SummaryApi.getMyAssignedInterviews.url);
      if (response.data.success) {
        setInterviews(response.data.data || []);
      }
    } catch (err) {
      toast.error('Failed to load assigned interviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyInterviews();
  }, [fetchMyInterviews]);

  // Open feedback modal for a specific interview
  const handleOpenFeedback = (item) => {
    setSelectedItem(item);
    setFeedbackForm({ rating: 3, comments: '', recommendation: 'yes' });
    setIsFeedbackOpen(true);
  };

  // Close feedback modal
  const handleCloseFeedback = () => {
    setIsFeedbackOpen(false);
    setSelectedItem(null);
  };

  // Handle feedback form submission
  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackForm.comments.trim()) {
      toast.warning('Please enter evaluation remarks');
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const { candidateId, interviewDetails } = selectedItem;
      // Submits feedback to: POST /recruitment/candidates/:id/interviews/:interviewId/feedback
      const response = await api.post(
        SummaryApi.candidateSubmitInterviewFeedback.url(candidateId, interviewDetails.interviewId),
        feedbackForm
      );

      if (response.data.success) {
        toast.success('Evaluation feedback submitted successfully');
        handleCloseFeedback();
        fetchMyInterviews(); // Refresh list to remove completed round
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  // Filter interviews dynamically based on search query
  const filteredInterviews = useMemo(() => {
    return interviews.filter(item => {
      const fullName = `${item.firstName} ${item.lastName}`.toLowerCase();
      const jobTitle = (item.jobTitle || '').toLowerCase();
      const appNumber = (item.applicationId || '').toLowerCase();
      const query = searchQuery.toLowerCase();
      return fullName.includes(query) || jobTitle.includes(query) || appNumber.includes(query);
    });
  }, [interviews, searchQuery]);

  // Helper to format date cleanly
  const formatScheduledDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-IN', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  if (loading) return <PageLoader />;

  return (
   <>
    <div className="space-y-6">
      {/* Header section */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">My Pending Evaluations</h1>
          <p className="text-xs text-gray-500 mt-1">Below are the candidate interviews assigned to you requiring feedback submission.</p>
        </div>
        
        {/* Search filter input */}
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search candidate or job..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-xs outline-none focus:ring-2 focus:ring-red-500 transition-all bg-gray-50/50"
          />
        </div>
      </div>

      {/* Main card grid */}
      {filteredInterviews.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredInterviews.map((item) => (
            <div 
              key={item.interviewDetails.interviewId} 
              className="bg-white border border-gray-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-all flex flex-col justify-between space-y-4"
            >
              {/* Candidate Info Header */}
              <div className="flex justify-between items-start">
                <div>
                  <span className="px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-red-50 text-primary border border-red-100">
                    ROUND {item.interviewDetails.round}
                  </span>
                  <h3 className="font-bold text-gray-900 text-sm mt-1.5 capitalize">
                    {item.firstName} {item.lastName}
                  </h3>
                  <p className="text-[10px] text-gray-400 mt-0.5">{item.applicationId}</p>
                </div>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 text-gray-700 capitalize border">
                  {item.interviewDetails.roundName || 'Technical'}
                </span>
              </div>

              {/* Job Designation details */}
              <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 text-xs">
                <span className="text-[9px] font-bold text-gray-400 uppercase block">Assigned Position</span>
                <p className="font-semibold text-gray-700 truncate mt-0.5">{item.jobTitle || 'N/A'}</p>
              </div>

              {/* Timeline details */}
              <div className="space-y-2 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <Calendar className="h-3.5 w-3.5 text-gray-400" />
                  <span>{formatScheduledDate(item.interviewDetails.scheduledAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  <span>Duration: {item.interviewDetails.durationMinutes} mins</span>
                </div>
                <div className="flex items-center gap-2">
                  {item.interviewDetails.mode === 'online' ? (
                    <>
                      <Video className="h-3.5 w-3.5 text-green-500" />
                      <span className="text-green-600 font-semibold">Online Meeting</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="h-3.5 w-3.5 text-gray-400" />
                      <span className="truncate">{item.interviewDetails.location || 'Office Location'}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Call to actions footer */}
              <div className="flex gap-2 pt-2 border-t">
                {item.interviewDetails.mode === 'online' && item.interviewDetails.meetingLink && (
                  <a 
                    href={item.interviewDetails.meetingLink} 
                    target="_blank" 
                    rel="noreferrer" 
                    className="flex-1 flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 py-2 rounded-lg text-[11px] font-bold transition-all"
                  >
                    Join <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                <button 
                  onClick={() => handleOpenFeedback(item)}
                  className="flex-1 bg-primary hover:bg-red-700 text-white py-2 rounded-lg text-[11px] font-bold transition-all flex items-center justify-center gap-1"
                >
                  <Plus className="h-3 w-3" /> Feedback
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white border rounded-xl p-12 flex flex-col items-center justify-center text-center shadow-sm space-y-4">
          <div className="p-3 bg-red-50 rounded-full text-primary border border-red-100">
            <CheckCircle className="h-8 w-8 animate-bounce" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-sm">No Pending Evaluations</h3>
            <p className="text-xs text-gray-400 mt-1">Excellent job! You have submitted all of your assigned evaluations. There are no pending feedback tasks at this time.</p>
          </div>
        </div>
      )}
    </div>
      {/* Submit Evaluation Feedback Offcanvas drawer */}
      <FormOffCanvas 
        isOpen={isFeedbackOpen} 
        onClose={handleCloseFeedback} 
        title="Submit Interview Evaluation"
      >
        {selectedItem && (
          <form onSubmit={handleFeedbackSubmit} className="space-y-4">
            
            {/* Candidate Metadata Summary */}
            <div className="bg-gray-50 p-3.5 rounded-lg border border-gray-100 space-y-1.5">
              <span className="text-[9px] font-extrabold text-primary uppercase">Candidate Under Review</span>
              <p className="text-xs font-bold text-gray-800 capitalize">{selectedItem.firstName} {selectedItem.lastName}</p>
              <p className="text-[10px] text-gray-500">Round {selectedItem.interviewDetails.round} — {selectedItem.interviewDetails.roundName || 'Technical Evaluation'}</p>
            </div>

            {/* Rating Selector */}
            <SelectDropdown
              label="Candidate Rating *"
              options={ratingOptions}
              value={feedbackForm.rating}
              onChange={(val) => setFeedbackForm(prev => ({ ...prev, rating: Number(val) }))}
              valueKey="value"
              labelKey="label"
              searchable={false}
              zIndex={69}
              multiple={false}
            />

            {/* Recommendation Selector */}
            <SelectDropdown
              label="Hire Recommendation *"
              options={recommendationOptions}
              value={feedbackForm.recommendation}
              onChange={(val) => setFeedbackForm(prev => ({ ...prev, recommendation: val }))}
              valueKey="value"
              labelKey="label"
              searchable={false}
              zIndex={68}
              multiple={false}
            />

            {/* Comment Section Textarea */}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1">Technical Remarks & Comments *</label>
              <textarea 
                required 
                value={feedbackForm.comments} 
                onChange={(e) => setFeedbackForm(prev => ({ ...prev, comments: e.target.value }))} 
                className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500" 
                rows={5} 
                placeholder="Describe candidate performance, key strengths, weaknesses, and rationale for your decision..." 
              />
            </div>

            <button 
              type="submit" 
              disabled={isSubmittingFeedback} 
              className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              {isSubmittingFeedback ? 'Submitting Evaluation...' : 'Register Evaluation Feedback'}
            </button>
          </form>
        )}
      </FormOffCanvas>
    </>
  );
};

export default MyAssignedInterviews;